import React, { useState, useEffect } from 'react';

// `href` is not decoration. Crawlers discover a site by following <a href>;
// an onClick-only <button> is invisible to them, which previously left every
// page except "/" undiscoverable except via the sitemap. The click handler
// still does client-side navigation — the href is the crawlable fallback and
// is what makes middle-click / open-in-new-tab work.
const NAV_ITEMS = [
  { id: 'home', label: 'Home', href: '/', title: 'LEAD-AI home' },
  { id: 'crm', label: 'CRM', href: '/CRM', title: 'Real estate CRM for Indian teams' },
  { id: 'leads', label: 'Leads Platform', href: '/lead-platform', title: 'AI lead generation platform — 400M+ contacts' },
  { id: 'xfactor', label: 'The X Factor', href: '/The-X-Factor', title: 'Precision lead matching vs Meta and Google Ads' },
  { id: 'about', label: 'About Us', href: '/About-us', title: 'About LEAD-AI' },
  { id: 'pricing', label: 'Pricing', href: '/Pricing', title: 'LEAD-AI pricing — free for 12 months' },
  { id: 'blog', label: 'Blog', href: '/blog', title: 'Real estate CRM and lead generation guides' },
];

export default function Nav({ activePage, onNavigate, onTrialClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (event, id) => {
    // Let the browser handle modified clicks (new tab / new window) natively.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onNavigate(id);
    setMobileOpen(false);
  };

  return (
    <>
      <nav className="main-nav" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      height: 64, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '0 48px',
      transition: 'all 0.4s',
      background: scrolled ? 'rgba(255,255,255,0.85)' : 'transparent',
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
      borderBottom: scrolled ? '1px solid var(--border)' : 'none',
    }}>
      {/* Logo */}
      <a
        href="/"
        onClick={(e) => handleNav(e, 'home')}
        aria-label="LEAD-AI home"
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
      >
        <div style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" fill="none" stroke="#fff" strokeWidth="2.5" viewBox="0 0 24 24">
            <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/>
            <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/>
            <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/>
          </svg>
        </div>
        <span style={{ fontSize: 19, fontWeight: 800, letterSpacing: -0.5 }}>LEAD-AI</span>
      </a>

      {/* Desktop Nav Links */}
      <div style={{ display: 'flex', gap: 4 }} className="nav-links-desktop">
        {NAV_ITEMS.map((item) => (
          <a
            key={item.id}
            href={item.href}
            title={item.title}
            aria-current={activePage === item.id ? 'page' : undefined}
            onClick={(e) => handleNav(e, item.id)}
            style={{
              fontSize: 13, fontWeight: 500, textDecoration: 'none',
              color: activePage === item.id ? 'var(--text)' : 'var(--text3)',
              background: activePage === item.id ? 'rgba(0,0,0,0.06)' : 'none',
              border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              padding: '8px 16px', borderRadius: 100, transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.target.style.color = 'var(--text)'; e.target.style.background = 'rgba(0,0,0,0.04)'; }}
            onMouseLeave={e => {
              e.target.style.color = activePage === item.id ? 'var(--text)' : 'var(--text3)';
              e.target.style.background = activePage === item.id ? 'rgba(0,0,0,0.06)' : 'none';
            }}
          >
            {item.label}
          </a>
        ))}
      </div>

      {/* CTA Buttons */}
      <div className="nav-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div className="nav-cta-desktop" style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary" onClick={onTrialClick}>Start Free →</button>
        </div>

      {/* Top Hamburger is removed for mobile/tablet, replaced by bottom nav */}
      </div>
    </nav>

      {/* ── Floating Bottom Mobile Nav ── */}
      <div className="mobile-floating-nav" style={{
        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        display: 'none', alignItems: 'center', gap: 16, zIndex: 1100,
        background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.15)',
        padding: '6px 6px 6px 20px', borderRadius: 100,
        boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(0,0,0,0.2)'
      }}>
        {/* LEAD-AI Title */}
        <a
          href="/"
          onClick={(e) => handleNav(e, 'home')}
          aria-label="LEAD-AI home"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', paddingLeft: 4, paddingRight: 6, textDecoration: 'none' }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.5 }}>LEAD-AI</span>
        </a>

        {/* Hamburger Modal Trigger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ 
            background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', 
            width: 38, height: 38, borderRadius: '50%', display: 'flex', flexDirection: 'column', 
            justifyContent: 'center', alignItems: 'center', gap: 4, transition: 'all 0.2s',
            backdropFilter: 'blur(10px)'
          }}
          aria-label="Menu"
        >
          <span style={{ display: 'block', width: 14, height: 2, background: '#fff', borderRadius: 2, transition: 'all 0.3s', transform: mobileOpen ? 'rotate(45deg) translate(4.5px, 4.5px)' : 'none' }}></span>
          <span style={{ display: 'block', width: 14, height: 2, background: '#fff', borderRadius: 2, transition: 'all 0.3s', opacity: mobileOpen ? 0 : 1 }}></span>
          <span style={{ display: 'block', width: 14, height: 2, background: '#fff', borderRadius: 2, transition: 'all 0.3s', transform: mobileOpen ? 'rotate(-45deg) translate(4px, -4px)' : 'none' }}></span>
        </button>
      </div>

      {/* Mobile Menu Backdrop & Sheet */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(3px)', zIndex: 1090 }} onClick={() => setMobileOpen(false)} />
          
          <div style={{
            position: 'fixed', bottom: 84, left: 16, right: 16,
            background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(30px)',
            border: '1px solid var(--border)', borderRadius: 24,
            padding: '16px', display: 'flex', flexDirection: 'column', gap: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)', zIndex: 1099,
            animation: 'slideUpNav 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          }}>
            {NAV_ITEMS.map((item) => (
              <a
                key={item.id}
                href={item.href}
                title={item.title}
                aria-current={activePage === item.id ? 'page' : undefined}
                onClick={(e) => handleNav(e, item.id)}
                style={{
                  fontSize: 15, fontWeight: 500, textDecoration: 'none',
                  color: activePage === item.id ? 'var(--text)' : 'var(--text2)',
                  background: activePage === item.id ? 'rgba(79,82,248,0.06)' : 'none',
                  border: 'none', borderLeft: activePage === item.id ? '3px solid var(--blue)' : '3px solid transparent',
                  cursor: 'pointer', fontFamily: 'inherit',
                  padding: '13px 16px', borderRadius: 10, textAlign: 'left',
                  transition: 'all 0.2s',
                }}
              >
                {item.label}
              </a>
            ))}
            <div style={{ display: 'flex', gap: 8, padding: '12px 0 0', borderTop: '1px solid var(--border)', marginTop: 8 }}>
              <button className="btn-primary" style={{ flex: 1, justifyContent: 'center', minHeight: 44 }} onClick={onTrialClick}>Start Free →</button>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideUpNav {
          0% { opacity: 0; transform: translateY(20px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* ── Mobile (≤767px) ── */
        @media (max-width: 767px) {
          .main-nav { padding: 0 20px !important; justify-content: center !important; }
          .nav-links-desktop { display: none !important; }
          .nav-cta-desktop { display: none !important; }
          .nav-right { display: none !important; }
          .mobile-floating-nav { display: flex !important; }
        }

        /* ── Tablet (768–1024px): hide nav links, keep CTAs ── */
        @media (min-width: 768px) and (max-width: 1024px) {
          .main-nav { padding: 0 32px !important; }
          .nav-links-desktop { display: none !important; }
          .nav-cta-desktop { display: flex !important; gap: 8px !important; }
          .mobile-floating-nav { display: flex !important; }
          .nav-right { gap: 12px !important; }
          .nav-cta-desktop .btn-secondary {
            height: 40px !important;
            padding: 0 16px !important;
            font-size: 13px !important;
            min-height: unset !important;
          }
          .nav-cta-desktop .btn-primary {
            height: 40px !important;
            padding: 0 18px !important;
            font-size: 13px !important;
            min-height: unset !important;
          }
        }

        /* ── Desktop (≥1025px): full nav ── */
        @media (min-width: 1025px) {
          .mobile-floating-nav { display: none !important; }
        }

        /* ── Wide tablet / small desktop (1025–1279px) ── */
        @media (min-width: 1025px) and (max-width: 1279px) {
          .main-nav { padding: 0 40px !important; }
        }
      `.trim()}</style>
    </>
  );
}
